import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

export interface ConfirmDialogFilter {
  column: string;
  operator: string;
  value: string;
}

export interface ConfirmDialogBlock {
  label: string;
  primary?: string;
  notes?: string[];
  items?: string[];
  filters?: ConfirmDialogFilter[];
}

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Danger dialogs keep the warning icon and red action. Neutral is for non-destructive confirms. */
  tone?: 'danger' | 'neutral';
  /** Structured body. Operators render in their own color. */
  blocks?: ConfirmDialogBlock[];
}

@Component({
  selector: 'app-confirm-popup',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <div class="confirm-dialog-container" [style.width.px]="getDialogWidth()">
      <div class="dialog-header">
        <div class="warning-icon" *ngIf="data.tone !== 'neutral'">
          <img src="assets/icons/playground/red_warning_icon.svg" alt="warning" width="24" height="24">
        </div>
        <h3 class="dialog-title" [class.wrap]="data.tone === 'neutral'">{{ data.title }}</h3>
      </div>

      <div class="dialog-content scrollable" *ngIf="data.blocks?.length; else plainMessage">
        <section class="confirm-block" *ngFor="let block of data.blocks">
          <div class="confirm-label">{{ block.label }}</div>
          <div class="confirm-primary" *ngIf="block.primary">{{ block.primary }}</div>
          <p class="confirm-note" *ngFor="let note of block.notes">{{ note }}</p>
          <ul class="confirm-items" *ngIf="block.items?.length">
            <li *ngFor="let item of block.items">{{ item }}</li>
          </ul>
          <div class="filter-row" *ngFor="let filter of block.filters">
            <span class="filter-column">{{ filter.column }}</span>
            <span class="filter-operator" *ngIf="filter.operator">{{ filter.operator }}</span>
            <span class="filter-value" *ngIf="filter.value">{{ filter.value }}</span>
          </div>
        </section>
      </div>
      <ng-template #plainMessage>
        <div class="dialog-content">
          <p class="dialog-message">{{ data.message }}</p>
        </div>
      </ng-template>

      <div class="dialog-actions">
        <button type="button" class="cancel-btn" (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button type="button" class="confirm-btn" [class.neutral]="data.tone === 'neutral'" (click)="onConfirm()">
          {{ data.confirmText || 'Yes' }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .confirm-dialog-container {
        background: white;
        border-radius: 8px;
        padding: 20px;
        min-width: 480px;
        max-width: 90vw;
        max-height: 90vh;
        box-shadow:
          0px 8px 10px 0px rgba(0, 0, 0, 0.2),
          0px 16px 24px 0px rgba(0, 0, 0, 0.14),
          0px 6px 30px 0px rgba(0, 0, 0, 0.12);
        overflow: visible;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: break-word;
        display: flex;
        flex-direction: column;
      }

      .dialog-header {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 20px;
        min-width: 0;
      }

      .warning-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .dialog-title {
        margin: 0;
        font-family: 'Lexend', sans-serif;
        font-size: 20px;
        font-weight: 600;
        color: #1e2225;
        line-height: 23px;
        letter-spacing: 0px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 1;
      }

      .dialog-title.wrap {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
        font-size: 18px;
        line-height: 26px;
      }

      .dialog-content.scrollable {
        max-height: min(420px, 52vh);
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-right: 6px;
      }

      .confirm-block + .confirm-block {
        margin-top: 16px;
      }

      .confirm-label {
        font-family: 'Lexend', sans-serif;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #8b95a1;
        margin-bottom: 6px;
      }

      .confirm-primary {
        font-family: 'Lexend', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: #1e2225;
        line-height: 22px;
      }

      .confirm-note {
        margin: 4px 0 0;
        font-family: 'Lexend', sans-serif;
        font-size: 13px;
        line-height: 20px;
        color: #6b7280;
      }

      .confirm-items {
        margin: 6px 0 0;
        padding-left: 18px;
        font-family: 'Lexend', sans-serif;
        font-size: 13px;
        line-height: 22px;
        color: #1e2225;
      }

      .filter-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
        padding: 8px 10px;
        background: #f6f7fb;
        border-radius: 8px;
      }

      .filter-column {
        font-family: 'Lexend', sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #1e2225;
      }

      .filter-operator {
        font-family: 'Lexend', sans-serif;
        font-size: 12px;
        font-weight: 600;
        line-height: 18px;
        color: #c2410c;
        background: #fff4e5;
        border-radius: 999px;
        padding: 2px 8px;
      }

      .filter-value {
        font-family: 'Lexend', sans-serif;
        font-size: 13px;
        color: #475258;
        line-height: 20px;
        min-width: 0;
        word-break: break-word;
      }

      .dialog-content {
        margin-bottom: 20px;
        min-width: 0;
        flex: 1;
        overflow: visible;
      }

      .dialog-message {
        margin: 0;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        color: #475258;
        line-height: 24px;
        letter-spacing: -0.16px;
        white-space: pre-line;
        word-wrap: break-word;
        overflow-wrap: break-word;
        min-width: 0;
        max-width: 100%;
        hyphens: auto;
        overflow: visible;
      }

      .dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        width: 100%;
        margin-top: 0;
        min-width: 0;
        flex-shrink: 0;
      }

      .cancel-btn,
      .confirm-btn {
        height: 32px;
        padding: 5px 8px;
        border-radius: 4px;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;
        border: none;
        line-height: 24px;
        letter-spacing: -0.16px;
        transition: all 0.2s ease;
        min-width: 60px;
        flex-shrink: 0;
      }

      .cancel-btn {
        background: #ebeff5;
        color: #475258;
      }

      .cancel-btn:hover {
        background: #dde5ed;
      }

      .confirm-btn {
        background: #FF4444;
        color: white;
      }

      .confirm-btn:hover {
        background: #e63939;
      }

      .confirm-btn.neutral {
        background: #534ad8;
      }

      .confirm-btn.neutral:hover {
        background: #4339c7;
      }

      /* Global overlay styles */
      ::ng-deep .cdk-overlay-backdrop {
        background: var(--background-variant-81-e-2225, #1E2225) !important;
        opacity: 0.4 !important;
      }

      ::ng-deep .cdk-overlay-pane {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .mat-mdc-dialog-container {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .mat-mdc-dialog-surface {
        overflow: visible !important;
        padding: 0 !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      /* Dialog panel specific styles */
      ::ng-deep .confirm-dialog-panel {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .confirm-dialog-backdrop {
        background: var(--background-variant-81-e-2225, #1E2225) !important;
        opacity: 0.4 !important;
      }

      /* Ensure no horizontal scrollbars and proper sizing */
      ::ng-deep .mat-mdc-dialog-container,
      ::ng-deep .mat-mdc-dialog-surface,
      ::ng-deep .cdk-overlay-pane {
        max-width: 90vw !important;
        max-height: 90vh !important;
        overflow: visible !important;
      }
    `,
  ],
})
export class ConfirmPopupComponent {
  private readonly BASE_WIDTH = 480;
  private readonly MIN_WIDTH_PER_CHAR = 8; // Approximate pixels per character
  private readonly MAX_WIDTH = 800; // Maximum width before wrapping
  private readonly PADDING = 40; // Account for padding and margins

  constructor(
    public dialogRef: MatDialogRef<ConfirmPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  getDialogWidth(): number {
    if (this.data?.blocks?.length) {
      return 560;
    }
    if (!this.data?.message) {
      return this.BASE_WIDTH;
    }

    // Calculate approximate width needed for the message
    // Consider line breaks and average character width
    const messageLength = this.data.message.length;
    const estimatedWidth = messageLength * this.MIN_WIDTH_PER_CHAR + this.PADDING;

    // Use base width for short messages
    if (estimatedWidth <= this.BASE_WIDTH) {
      return this.BASE_WIDTH;
    }

    // For longer messages, increase width but cap at max width
    const calculatedWidth = Math.min(estimatedWidth, this.MAX_WIDTH);

    // Round to nearest 20px for cleaner appearance
    const finalWidth = Math.ceil(calculatedWidth / 20) * 20;

    // Ensure minimum width and reasonable maximum
    return Math.max(this.BASE_WIDTH, Math.min(finalWidth, this.MAX_WIDTH));
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
